// src/Pages/Purchase/PurchaseData/AddExpenses.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_URL } from "../../../config";
import "./AddExpenses.css";

const AddExpenses = () => {
  const navigate = useNavigate();
  const [expenseNo, setExpenseNo] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [expenseAccount, setExpenseAccount] = useState("");
  const [itemized, setItemized] = useState(false);
  const [currency, setCurrency] = useState("INR");
  const [singleAmount, setSingleAmount] = useState(0);
  const [items, setItems] = useState([]);
  const [paidThrough, setPaidThrough] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [notes, setNotes] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [reportingTags, setReportingTags] = useState([]);

  // Options
  const accounts = [
    "Job Costing",
    "Cost of Goods Sold",
    "Labor",
    "Materials",
    "Bad Debt",
    "Fixed Asset",
  ];
  const currencies = ["INR", "AUD", "BND", "CAD", "SAR", "USD", "ZAR"];
  const paidThroughOptions = [
    "Petty Cash",
    "Undeposited Funds",
    "Advance Tax",
    "Employee Advance",
  ];

  // Fetch next expense #
  useEffect(() => {
    fetchNextExpenseNo();
  }, []);

  const fetchNextExpenseNo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/expenses/next-expense-no`, {
        credentials: "include",
      });
      const data = await res.json();
      setExpenseNo(data.success ? data.nextExpenseNo : "EXP-000001");
    } catch {
      setExpenseNo("EXP-000001");
    }
  };

  // Fetch vendors & customers
  useEffect(() => {
    const backendUrl =
      process.env.REACT_APP_BACKEND_URL ||
      (process.env.NODE_ENV === "production"
        ? "https://finance.dev.marsforce.in"
        : "http://localhost:8030");
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Vendors
        const vendorsRes = await fetch(
          `${backendUrl}/api/vendors/get?userId=${userId}`,
          { credentials: "include" }
        );
        const vendorsData = await vendorsRes.json();
        if (vendorsData.success) {
          setVendors(
            vendorsData.data.sort((a, b) =>
              getVendorDisplayName(a).localeCompare(getVendorDisplayName(b))
            )
          );
        }

        // Customers
        const customersRes = await fetch(
          `${backendUrl}/api/customers/get?userId=${userId}`,
          { credentials: "include" }
        );
        const customersData = await customersRes.json();
        if (customersData.success) {
          setCustomers(
            customersData.data.sort((a, b) =>
              getCustomerDisplayName(a).localeCompare(getCustomerDisplayName(b))
            )
          );
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getVendorDisplayName = (vendor) => {
    const fullName = [vendor.firstName, vendor.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const primary =
      fullName || vendor.displayName || vendor.companyName || "Unnamed";
    const secondary =
      vendor.companyName && vendor.companyName !== primary
        ? ` (${vendor.companyName})`
        : "";
    return primary + secondary;
  };

  const getCustomerDisplayName = (cust) => {
    const fullName = [cust.firstName, cust.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const primary =
      fullName || cust.displayName || cust.companyName || "Unnamed";
    const secondary =
      cust.companyName && cust.companyName !== primary
        ? ` (${cust.companyName})`
        : "";
    return primary + secondary;
  };

  // Itemized logic
  useEffect(() => {
    if (itemized) {
      if (items.length === 0) {
        addItem();
      }
    } else {
      setItems([]);
    }
  }, [itemized]);

  const addItem = () => {
    const newItem = { id: Date.now(), account: "", description: "", amount: 0 };
    setItems([...items, newItem]);
  };

  const updateItem = (id, field, value) => {
    setItems(
      items.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter((item) => item.id !== id));
    }
  };

  const total = itemized
    ? items.reduce((sum, item) => sum + Number(item.amount || 0), 0)
    : Number(singleAmount || 0);

  const handleTagsChange = (e) => {
    setTagsInput(e.target.value);
  };

  const handleTagsBlur = () => {
    const newTags = tagsInput
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    setReportingTags([...reportingTags, ...newTags]);
    setTagsInput("");
  };

  const removeTag = (index) => {
    setReportingTags(reportingTags.filter((_, i) => i !== index));
  };

  const handleSave = async (saveAndNew = false) => {
    if (!expenseAccount && !itemized) return alert("Select Expense Account");
    if (total <= 0) return alert("Amount required");
    if (!paidThrough) return alert("Select Paid Through");

    const userId = localStorage.getItem("userId");
    const expenseItems = itemized
      ? items.map(({ account, description, amount }) => ({
          account,
          description,
          amount: Number(amount),
        }))
      : [{ account: expenseAccount, description: "", amount: total }];

    const expenseData = {
      expenseNo,
      date,
      vendorName: selectedVendor,
      customerName: selectedCustomer,
      paidThrough,
      invoiceNo,
      notes,
      reportingTags,
      currency,
      items: expenseItems,
      total,
      isItemized: itemized,
      status: "PAID",
      userId,
    };

    try {
      const res = await fetch(`${API_URL}/api/expenses/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(expenseData),
      });
      const data = await res.json();
      if (data.success) {
        if (saveAndNew) {
          fetchNextExpenseNo();
          resetForm();
        } else {
          navigate("/purchase/expenses");
        }
      } else {
        alert("Save failed: " + data.error);
      }
    } catch {
      alert("Network error");
    }
  };

  const resetForm = () => {
    setDate(new Date().toISOString().split("T")[0]);
    setExpenseAccount("");
    setItemized(false);
    setCurrency("INR");
    setSingleAmount(0);
    setItems([]);
    setPaidThrough("");
    setInvoiceNo("");
    setNotes("");
    setReportingTags([]);
    setTagsInput("");
    setSelectedVendor("");
    setSelectedCustomer("");
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="add-expense-page">
      <div className="expense-header">
        <h1>New Expense</h1>
      </div>
      <div className="expense-form">
        {/* Date & Expense # */}
        <div className="form-row">
          <div className="form-group">
            <label>
              Date <span className="required">*</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Expense#</label>
            <input type="text" value={expenseNo} readOnly />
          </div>
        </div>

        {/* Expense Account & Itemize */}
        <div className="form-group expense-account-group">
          <label>
            Expense Account <span className="required">*</span>
          </label>
          <select
            value={itemized ? "" : expenseAccount}
            onChange={(e) => setExpenseAccount(e.target.value)}
            disabled={itemized}
          >
            <option value="">Select an account</option>
            {accounts.map((acc) => (
              <option key={acc}>{acc}</option>
            ))}
          </select>
          <a
            href="#"
            className="itemize-link"
            onClick={(e) => {
              e.preventDefault();
              setItemized(!itemized);
            }}
          >
            {itemized ? "Single Expense" : "Itemize"}
          </a>
        </div>

        {/* Currency & Amount */}
        <div className="form-group">
          <label>
            Amount <span className="required">*</span>
          </label>
          <div className="currency-amount">
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {currencies.map((cur) => (
                <option key={cur}>{cur}</option>
              ))}
            </select>
            {!itemized ? (
              <input
                type="number"
                min="0"
                step="0.01"
                value={singleAmount}
                onChange={(e) => setSingleAmount(Number(e.target.value) || 0)}
                placeholder="0.00"
              />
            ) : (
              <span>{total.toFixed(2)}</span> // Total for itemized
            )}
          </div>
        </div>

        {/* Itemized Table */}
        {itemized && (
          <div className="item-section">
            <table className="item-table">
              <thead>
                <tr>
                  <th>ACCOUNT</th>
                  <th>DESCRIPTION</th>
                  <th>AMOUNT</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <select
                        value={item.account}
                        onChange={(e) =>
                          updateItem(item.id, "account", e.target.value)
                        }
                      >
                        <option value="">Select</option>
                        {accounts.map((acc) => (
                          <option key={acc}>{acc}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        value={item.description}
                        onChange={(e) =>
                          updateItem(item.id, "description", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        value={item.amount}
                        onChange={(e) =>
                          updateItem(
                            item.id,
                            "amount",
                            Number(e.target.value) || 0
                          )
                        }
                      />
                    </td>
                    <td>
                      <button onClick={() => removeItem(item.id)}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button onClick={addItem}>+ Add Row</button>
          </div>
        )}

        {/* Paid Through */}
        <div className="form-group">
          <label>
            Paid Through <span className="required">*</span>
          </label>
          <select
            value={paidThrough}
            onChange={(e) => setPaidThrough(e.target.value)}
          >
            <option value="">Select an account</option>
            {paidThroughOptions.map((opt) => (
              <option key={opt}>{opt}</option>
            ))}
          </select>
        </div>

        {/* Vendor */}
        <div className="form-group">
          <label>Vendor</label>
          <select
            value={selectedVendor}
            onChange={(e) => setSelectedVendor(e.target.value)}
          >
            <option value="">Select a vendor</option>
            {vendors.map((v) => (
              <option key={v._id} value={getVendorDisplayName(v)}>
                {getVendorDisplayName(v)}
              </option>
            ))}
          </select>
          <Link to="/purchase/vendors/new" className="add-link">
            + Add Vendor
          </Link>
        </div>

        {/* Invoice # */}
        <div className="form-group">
          <label>Invoice#</label>
          <input
            type="text"
            value={invoiceNo}
            onChange={(e) => setInvoiceNo(e.target.value)}
          />
        </div>

        {/* Notes */}
        <div className="form-group">
          <label>Notes</label>
          <textarea
            maxLength={500}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Max 500 characters"
          />
        </div>

        {/* Customer Name */}
        <div className="form-group">
          <label>Customer Name</label>
          <select
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
          >
            <option value="">Select or add a customer</option>
            {customers.map((c) => (
              <option key={c._id} value={getCustomerDisplayName(c)}>
                {getCustomerDisplayName(c)}
              </option>
            ))}
          </select>
          <Link to="/sales/customers/new" className="add-link">
            + Add Customer
          </Link>
        </div>

        {/* Reporting Tags */}
        <div className="form-group">
          <label>Reporting Tags</label>
          <div className="tags-container">
            {reportingTags.map((tag, i) => (
              <span key={i} className="tag-chip">
                {tag} <button onClick={() => removeTag(i)}>×</button>
              </span>
            ))}
            <input
              value={tagsInput}
              onChange={handleTagsChange}
              onBlur={handleTagsBlur}
              placeholder="Add tags, comma separated"
            />
          </div>
        </div>
        <div className="action-bar">
          <div>
            <button onClick={() => handleSave(false)} accessKey="s">
              Save (Alt+S)
            </button>
            <button onClick={() => handleSave(true)} accessKey="n">
              Save and New (Alt+N)
            </button>
            <button onClick={() => navigate("/purchase/expenses")}>
              Cancel
            </button>
          </div>
          <div className="total-display">
            Total:{" "}
            <strong>
              {currency} {total.toFixed(2)}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddExpenses;
